import { defaultMeasureParameterSelections, initializeDashboardRuntimeState } from '../../runtime'
import { resolveLineChartDateSortKey } from '../../sortUtils'
import type { FieldFormat } from '../../../../lib/fieldFormat'
import type { HydratedWidgetResult } from '../../widgetData'
import type {
  AgentBuilderChartDataPoint,
  AgentBuilderKpiCard,
  DashboardDefinition,
  DashboardRuntimeDateRange,
  DashboardWidget,
  DatePresetOption,
} from '../../types'

export const DEFAULT_WIDGET_PAGE_SIZE = 20
export const LARGE_WIDGET_PAGE_SIZE = 100

function resolveDefaultWidgetPageSize(widget?: DashboardWidget) {
  if (!widget) return DEFAULT_WIDGET_PAGE_SIZE
  return widget.type === 'line' || widget.type === 'table' || widget.type === 'dataBarTable' || widget.type === 'pivotTable'
    ? LARGE_WIDGET_PAGE_SIZE
    : DEFAULT_WIDGET_PAGE_SIZE
}

export type DashboardGlobalFilterState = {
  slicerValues: Record<string, string | string[] | null>
  dateRange: DashboardRuntimeDateRange
}

export type WidgetDrilldownState = {
  field: string
  value: string
  dimension: string
}

export type WidgetQueryState = {
  page: number
  pageSize: number
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  groupByDimension: string | null
  groupByDimensions: string[] | null
  drilldown: WidgetDrilldownState | null
  measureParameterSelections: Record<string, string> | null
}

export type WidgetRequestCause =
  | 'initial-load'
  | 'global-filter-apply'
  | 'refresh-all'
  | 'sort'
  | 'page'
  | 'dimension'
  | 'drilldown'
  | 'clear-drilldown'
  | 'measure-parameter'
  | 'view-apply'

export type WidgetRequestIntent = {
  widgetId: string
  cause: WidgetRequestCause
  query: WidgetQueryState
  appliedFilters: DashboardGlobalFilterState
}

export type WidgetSessionData = {
  rows: Record<string, unknown>[]
  columns: string[]
  chartData: AgentBuilderChartDataPoint[]
  comparisonChartData: AgentBuilderChartDataPoint[]
  kpiCards: AgentBuilderKpiCard[]
  daxQuery: string | null
  hasPaginationMetadata: boolean
  totalRows: number
  page: number
  pageSize: number
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  groupByDimension: string | null
  axisMinValue: number | null
  axisMaxValue: number | null
  axisMinByMeasure: Record<string, number> | null
  axisMaxByMeasure: Record<string, number> | null
  columnFormats: Record<string, FieldFormat>
  lastUpdatedAt: string | null
}

export type WidgetSessionState = {
  status: 'idle' | 'loading' | 'refreshing' | 'live' | 'error'
  error: string | null
  query: WidgetQueryState
  request: {
    requestId: number
    inFlight: boolean
    currentIntentKey: string | null
    queuedIntent: WidgetRequestIntent | null
    lastCompletedIntentKey: string | null
  }
  data: WidgetSessionData
  ui: {
    pendingSort: {
      column: string | null
      direction: 'asc' | 'desc'
    } | null
    pickerState: {
      field: string
      value: string
    } | null
  }
}

export type DashboardViewState = {
  dashboard: DashboardDefinition | null
  dashboardId: string | null
  dashboardLoad: {
    bootstrapStatus: 'idle' | 'loading' | 'ready' | 'error'
    error: string | null
    refreshNonce: number
    initialLoadCompleted: boolean
  }
  draftFilters: DashboardGlobalFilterState
  appliedFilters: DashboardGlobalFilterState
  slicerOptions: {
    byField: Record<string, string[]>
    loadingByField: Record<string, boolean>
    errorByField: Record<string, string | null>
    requestKeyByField: Record<string, string>
  }
  widgets: Record<string, WidgetSessionState>
}

export type DashboardViewAction =
  | { type: 'BOOTSTRAP_STARTED' }
  | { type: 'BOOTSTRAP_SUCCEEDED'; dashboard: DashboardDefinition; dashboardId: string | null }
  | {
      type: 'DASHBOARD_REPLACED_SELECTIVE'
      dashboard: DashboardDefinition
      dashboardId: string | null
      preserveWidgetIds: string[]
    }
  | { type: 'BOOTSTRAP_FAILED'; error: string }
  | { type: 'SET_DASHBOARD_ID'; dashboardId: string | null }
  | { type: 'DRAFT_SLICER_CHANGED'; field: string; value: string | string[] | null }
  | { type: 'DRAFT_DATE_RANGE_CHANGED'; dateRange: DashboardRuntimeDateRange }
  | { type: 'DATE_DEFAULT_RESOLVED'; datePresetOptions: DatePresetOption[] }
  | { type: 'DRAFT_FILTERS_RESET' }
  | { type: 'APPLIED_FILTERS_COMMITTED' }
  | { type: 'SLICER_OPTIONS_LOAD_STARTED'; requestKeyByField: Record<string, string> }
  | { type: 'SLICER_OPTIONS_LOAD_SUCCEEDED'; field: string; options: string[]; requestKey: string }
  | { type: 'SLICER_OPTIONS_LOAD_FAILED'; field: string; error: string; requestKey: string }
  | { type: 'WIDGET_SORT_SET'; widgetId: string; column: string | null; direction: 'asc' | 'desc' }
  | { type: 'WIDGET_PAGE_SET'; widgetId: string; page: number }
  | { type: 'WIDGET_DIMENSION_SET'; widgetId: string; groupByDimension: string | null; groupByDimensions: string[] | null }
  | { type: 'WIDGET_MEASURE_PARAMETER_SET'; widgetId: string; selections: Record<string, string> | null }
  | { type: 'WIDGET_DRILLDOWN_SET'; widgetId: string; drilldown: WidgetDrilldownState }
  | { type: 'WIDGET_DRILLDOWN_CLEARED'; widgetId: string }
  | {
      type: 'WIDGET_PENDING_SORT_SET'
      widgetId: string
      pendingSort: {
        column: string | null
        direction: 'asc' | 'desc'
      } | null
    }
  | {
      type: 'WIDGET_PICKER_SET'
      widgetId: string
      pickerState: {
        field: string
        value: string
      } | null
    }
  | {
      type: 'WIDGET_REQUEST_STARTED'
      widgetId: string
      requestId: number
      intentKey: string
    }
  | { type: 'WIDGET_REQUEST_QUEUED'; widgetId: string; intent: WidgetRequestIntent }
  | {
      type: 'WIDGET_REQUEST_SUCCEEDED'
      widgetId: string
      requestId: number
      intentKey: string
      data: WidgetSessionData
    }
  | {
      type: 'WIDGET_REQUEST_FAILED'
      widgetId: string
      requestId: number
      intentKey: string
      error: string
    }
  | { type: 'WIDGET_REQUEST_FINISHED'; widgetId: string; requestId: number }
  | {
      type: 'VIEW_APPLIED'
      filters: DashboardGlobalFilterState
      widgetQueries: Record<string, WidgetQueryState>
    }
  | { type: 'REFRESH_ALL_STARTED' }
  | { type: 'REFRESH_ALL_FINISHED' }

export function createDashboardGlobalFilterState(
  dashboard: DashboardDefinition | null,
  datePresetOptions?: DatePresetOption[] | null,
): DashboardGlobalFilterState {
  const runtimeState = initializeDashboardRuntimeState(dashboard, datePresetOptions)
  return {
    slicerValues: runtimeState.slicerValues,
    dateRange: runtimeState.dateRange,
  }
}

export function createDefaultWidgetQueryState(widget?: DashboardWidget): WidgetQueryState {
  const defaultLineSortColumn = widget ? resolveLineChartDateSortKey(widget) : null
  const initialSelections = widget ? defaultMeasureParameterSelections(widget) : {}

  return {
    page: 1,
    pageSize: resolveDefaultWidgetPageSize(widget),
    sortColumn: defaultLineSortColumn,
    sortDirection: defaultLineSortColumn ? 'asc' : 'desc',
    groupByDimension: widget?.rowDimension ?? widget?.daxGenerationRequest?.dimensions?.[0] ?? null,
    groupByDimensions: widget?.rowDimension ?? widget?.daxGenerationRequest?.dimensions?.[0]
      ? [widget?.rowDimension ?? widget?.daxGenerationRequest?.dimensions?.[0] ?? ''].filter(Boolean)
      : null,
    drilldown: null,
    measureParameterSelections: Object.keys(initialSelections).length > 0 ? initialSelections : null,
  }
}

export function createEmptyWidgetSessionData(query?: WidgetQueryState): WidgetSessionData {
  return {
    rows: [],
    columns: [],
    chartData: [],
    comparisonChartData: [],
    kpiCards: [],
    daxQuery: null,
    hasPaginationMetadata: false,
    totalRows: 0,
    page: query?.page ?? 1,
    pageSize: query?.pageSize ?? DEFAULT_WIDGET_PAGE_SIZE,
    sortColumn: query?.sortColumn ?? null,
    sortDirection: query?.sortDirection ?? 'desc',
    groupByDimension: query?.groupByDimension ?? null,
    axisMinValue: null,
    axisMaxValue: null,
    axisMinByMeasure: null,
    axisMaxByMeasure: null,
    columnFormats: {},
    lastUpdatedAt: null,
  }
}

export function createWidgetSessionState(widget?: DashboardWidget): WidgetSessionState {
  const query = createDefaultWidgetQueryState(widget)
  return {
    status: 'idle',
    error: null,
    query,
    request: {
      requestId: 0,
      inFlight: false,
      currentIntentKey: null,
      queuedIntent: null,
      lastCompletedIntentKey: null,
    },
    data: createEmptyWidgetSessionData(query),
    ui: {
      pendingSort: null,
      pickerState: null,
    },
  }
}

export function createWidgetStateMap(dashboard: DashboardDefinition | null): Record<string, WidgetSessionState> {
  if (!dashboard) return {}
  return Object.fromEntries(
    dashboard.widgets.map((widget) => [widget.id, createWidgetSessionState(widget)]),
  )
}

export function createDashboardViewState(
  dashboard: DashboardDefinition | null,
  dashboardId: string | null,
): DashboardViewState {
  const initialFilters = createDashboardGlobalFilterState(dashboard)
  return {
    dashboard,
    dashboardId,
    dashboardLoad: {
      bootstrapStatus: dashboard ? 'ready' : dashboardId ? 'loading' : 'idle',
      error: null,
      refreshNonce: 0,
      initialLoadCompleted: false,
    },
    draftFilters: initialFilters,
    appliedFilters: initialFilters,
    slicerOptions: {
      byField: {},
      loadingByField: {},
      errorByField: {},
      requestKeyByField: {},
    },
    widgets: createWidgetStateMap(dashboard),
  }
}

export function hasWidgetData(data: WidgetSessionData) {
  return data.rows.length > 0
    || data.columns.length > 0
    || data.chartData.length > 0
    || data.kpiCards.length > 0
    || Boolean(data.daxQuery)
}

export function buildWidgetSessionDataFromResult(result: HydratedWidgetResult): WidgetSessionData {
  return {
    rows: result.rows,
    columns: result.columns,
    chartData: result.chartData,
    comparisonChartData: result.comparisonChartData,
    kpiCards: result.kpiCards,
    daxQuery: result.daxQuery,
    hasPaginationMetadata: result.hasPaginationMetadata,
    totalRows: result.totalRows,
    page: result.page,
    pageSize: result.pageSize,
    sortColumn: result.sortColumn,
    sortDirection: result.sortDirection,
    groupByDimension: result.groupByDimension,
    axisMinValue: result.axisMinValue,
    axisMaxValue: result.axisMaxValue,
    axisMinByMeasure: result.axisMinByMeasure,
    axisMaxByMeasure: result.axisMaxByMeasure,
    columnFormats: result.columnFormats ?? {},
    lastUpdatedAt: new Date().toISOString(),
  }
}
